import React, { useState, useCallback, useMemo } from "react";
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";

const chipSx = (active) => ({
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  fontSize: 13,
  borderRadius: "0.6rem",
  height: 34,
  px: 0.5,
  cursor: "pointer",
  transition: "opacity 0.15s ease",
  ...(active
    ? {
      backgroundColor: "hsla(150, 38%, 17%, 0.08)",
      color: "hsl(150 38% 17%)",
      borderColor: "hsl(150 38% 17%)",
      "&:hover": { opacity: 0.8 },
    }
    : {
      backgroundColor: "#fff",
      color: "hsl(150 30% 12%)",
      borderColor: "hsl(140 8% 88%)",
      "&:hover": {
        borderColor: "hsl(140 8% 82%)",
      },
    }),
});

const FilterChip = ({ def, selected, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const count = selected.length;

  const handleToggle = (value) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(next);
  };

  const options = def.options;

  return (
    <>
      <Chip
        variant="outlined"
        label={count > 0 ? `${def.label} (${count})` : def.label}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={chipSx(count > 0)}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              borderRadius: "0.75rem",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              minWidth: 220,
              maxHeight: 360,
              overflow: "auto",
            },
          },
        }}
      >
        <Box sx={{ py: 0.5 }}>
          {options.map((opt) => (
            <FormControlLabel
              key={opt.value}
              sx={{ display: "flex", mx: 0, px: 2, py: 0.25 }}
              control={
                <Checkbox
                  size="small"
                  disableRipple
                  checked={selected.includes(opt.value)}
                  onChange={() => handleToggle(opt.value)}
                  sx={{
                    color: "hsl(140 8% 82%)",
                    "&.Mui-checked": { color: "hsl(150 38% 17%)" },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: 13, color: "hsl(150 30% 12%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                  {opt.label}
                </Typography>
              }
            />
          ))}
        </Box>
      </Popover>
    </>
  );
};

export const EMPTY_FILTERS = {
  action: [],
  user: [],
  trackableType: [],
};

const ACTION_OPTIONS = [
  { label: "Created", value: "created" },
  { label: "Updated", value: "updated" },
  { label: "Deleted", value: "deleted" },
  { label: "Whitelisted", value: "whitelisted" },
  { label: "Blacklisted", value: "blacklisted" },
];

const TRACKABLE_TYPE_OPTIONS = [
  { label: "Candidate", value: "Candidate" },
  { label: "Recruiter", value: "Recruiter" },
  { label: "Company", value: "Company" },
  { label: "Pipeline", value: "ProfilePipeline" },
  { label: "External Profile", value: "ExternalProfile" },
  { label: "Job Application", value: "JobApplication" },
];

const InternalActivitiesTableFilters = ({ filters, onFiltersChange, users = [] }) => {
  const filterDefs = useMemo(
    () => {
      const defs = [
        {
          id: "action",
          label: "Action",
          options: ACTION_OPTIONS,
        },
        {
          id: "trackableType",
          label: "Target Type",
          options: TRACKABLE_TYPE_OPTIONS,
        },
      ];

      if (users.length > 0) {
        defs.push({
          id: "user",
          label: "User",
          options: users.map((u) => ({ label: u.full_name || u.email, value: String(u.id) })),
        });
      }

      return defs;
    },
    [users],
  );

  const totalActive = useMemo(
    () => Object.values(filters).reduce((sum, val) => sum + (Array.isArray(val) ? val.length : 0), 0),
    [filters],
  );

  const handleChange = useCallback(
    (filterId) => (newValues) => {
      onFiltersChange({ ...filters, [filterId]: newValues });
    },
    [filters, onFiltersChange],
  );

  const handleClearAll = useCallback(() => {
    onFiltersChange({ ...EMPTY_FILTERS });
  }, [onFiltersChange]);

  return (
    <Box
      sx={{
        px: 3,
        py: 1,
        backgroundColor: "#fff",
        borderBottom: "1px solid hsl(140 8% 88%)",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <FilterListIcon sx={{ fontSize: 18, color: "hsl(150 30% 12%)", opacity: 0.5, mr: 0.5 }} />

        {filterDefs.map((def) => (
          <FilterChip
            key={def.id}
            def={def}
            selected={filters[def.id] || []}
            onChange={handleChange(def.id)}
          />
        ))}

        {totalActive > 0 && (
          <Chip
            size="small"
            label="Clear all"
            deleteIcon={<CloseIcon sx={{ fontSize: "14px !important", color: "hsl(0 72% 51%) !important" }} />}
            onDelete={handleClearAll}
            onClick={handleClearAll}
            sx={{
              ml: 0.5,
              height: 28,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              fontSize: 12,
              color: "hsl(0 72% 51%)",
              borderColor: "hsl(0 72% 51%)",
              backgroundColor: "rgba(211,47,47,0.06)",
              "&:hover": { backgroundColor: "rgba(211,47,47,0.12)" },
            }}
            variant="outlined"
          />
        )}
      </Stack>
    </Box>
  );
};

export default InternalActivitiesTableFilters;